import React from 'react';
import clsx from 'clsx';
import {useSortable} from '@dnd-kit/sortable';
import {faEquals as EqualsIcon} from '@fortawesome/pro-solid-svg-icons';
import {type CellContext, type RowData} from '@tanstack/react-table';
import {useArrowFocusTrapItem} from '@netx/core-components/ArrowFocusTrapNext';
import {
	IconButton,
	type IconButtonProps,
} from '@netx/core-components/IconButton';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {splitEventHandler} from '@netx/core-next/index';
import {
	DRAG_HANDLE_CELL_CLASS_NAME,
	dragHandleCellClasses,
	DragHandleCellClasses,
	getDragHandleCellUtilityClass,
} from './dragHandleCellClasses';

type DragHandleCellOwnerState = WithClasses<
	DragHandleCellClasses,
	{
		isDragging?: boolean;
	}
>;

const useUtilityClasses = (ownerState: DragHandleCellOwnerState) => {
	const {classes, isDragging} = ownerState;
	const slots = {
		root: ['root', isDragging && 'isDragging'],
	};
	return composeClasses(slots, getDragHandleCellUtilityClass, classes);
};

const DragHandleCellRoot = styled(IconButton, {
	name: DRAG_HANDLE_CELL_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	cursor: 'grab',
	'&[aria-disabled="true"]': {
		cursor: 'not-allowed',
	},
	// [`&.${dragHandleCellClasses.isDragging}`]: {
	// 	backgroundColor: theme.palette.action.focus,
	// }
}));

export interface DragHandleCellProps<TData extends RowData>
	extends CellContext<TData, any>,
		WithClasses<DragHandleCellClasses> {
	disabled?: boolean;
	IconButtonProps?: Partial<IconButtonProps>;
}

export const DragHandleCellPrimitive = <Tdata extends RowData>({
	classes: _classes,
	className,
	disabled,
	row,
	IconButtonProps,
}: DragHandleCellProps<Tdata>) => {
	const arrowFocusTrapItem = useArrowFocusTrapItem({
		index: row.index,
	});

	const {attributes, isDragging, listeners} = useSortable({
		id: row.id,
	});

	const ownerState: DragHandleCellOwnerState = {
		classes: _classes,
		className,
		isDragging,
	};

	const _disabled = disabled || attributes['aria-disabled'];

	const classes = useUtilityClasses(ownerState);

	return (
		<DragHandleCellRoot
			className={clsx(classes.root, className)}
			disabled={_disabled}
			icon={EqualsIcon}
			label=""
			{...IconButtonProps}
			{...listeners}
			{...attributes}
			{...(_disabled ? null : arrowFocusTrapItem.actionAttributes)}
			onMouseDown={splitEventHandler(
				IconButtonProps?.onMouseDown,
				(event) => {
					if (_disabled) return;
					arrowFocusTrapItem.isActive && event.stopPropagation();
				},
				listeners?.onMouseDown as (event: React.MouseEvent) => void,
			)}
		/>
	);
};

export const DragHandleCell = DragHandleCellPrimitive;
